<script setup lang="ts">
import SmallModel from '@/components/Interface/Modal/ModelSizes/SmallModel.vue'
import { useGameStore } from '@/stores/gameStore.ts'
import { useAllianceStore } from '@/stores/allianceStore.ts'
import PlayerName from '@/components/Objects/PlayerName.vue'
import { computed, ref, watchEffect } from 'vue'
import Backend from '@/models/backend.ts'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import { logger } from '@/models/logger';
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const gameStore = useGameStore()
const allianceStore = useAllianceStore()
const backend = new Backend()

// Track request status locally
const requestPending = ref(false)

const alliance = computed(() => {
  const allianceId = gameStore.modalData?.allianceId
  return allianceId ? allianceStore.alliances.get(allianceId) : null
})

// Get member IDs
const memberIds = computed(() => {
  if (!alliance.value || !alliance.value.members) return []
  return alliance.value.members
})

// Check if player is already in this alliance
const isPlayerInThisAlliance = computed(() => {
  return memberIds.value.includes(gameStore.player?.id || 0)
})

// Check if player is available to join (not in any alliance)
const canRequestToJoin = computed(() => {
  return gameStore.player?.allianceId === 0 || !gameStore.player?.allianceId
})

// Check if player has already requested to join this alliance
const hasRequestedToJoin = computed(() => {
  return requestPending.value || alliance.value?.requestingToJoin === 1
})

// Initialize request status based on alliance data
watchEffect(() => {
  if (alliance.value) {
    requestPending.value = alliance.value.requestingToJoin === 1
  }
})

// Request to join alliance
const requestJoinAlliance = () => {
  if (!alliance.value) return

  // Set local state immediately to update UI
  requestPending.value = true

  backend
    .requestJoinAlliance(alliance.value.id)
    .then(() => {
      gameStore.setTinySuccessToastMessage(t('modals.viewAlliance.messages.joinRequestSent'))
      requestPending.value = true
      // Update alliance data
      if (alliance.value) {
        alliance.value.requestingToJoin = 1
      }
    })
    .catch((error) => {
      // Reset the local state on error
      requestPending.value = false
      gameStore.setTinyErrorToastMessage(t('modals.viewAlliance.messages.joinRequestFailed'))
      logger.error(error)
    })
}

// Revoke request to join alliance
const revokeJoinAlliance = () => {
  if (!alliance.value) return

  // Set local state immediately to update UI
  requestPending.value = false

  backend
    .revokeJoinAlliance(alliance.value.id)
    .then(() => {
      gameStore.setTinySuccessToastMessage(t('modals.viewAlliance.messages.joinRequestRevoked'))
      requestPending.value = false
      // Update alliance data
      if (alliance.value) {
        alliance.value.requestingToJoin = 0
      }
    })
    .catch((error) => {
      // Reset the local state on error
      requestPending.value = true
      gameStore.setTinyErrorToastMessage(t('modals.viewAlliance.messages.revokeRequestFailed'))
      logger.error(error)
    })
}
</script>

<template>
  <SmallModel :title="alliance ? alliance.name : t('modals.viewAlliance.title')">
    <div class="p-2">
      <!-- Alliance Image and Basic Info -->
      <div class="flex items-start mb-2">
        <div class="flex-shrink-0 mr-2">
          <img :src="alliance?.allianceImage" class="w-24 h-24 rounded" />
        </div>
        <div class="flex-grow flex flex-col relative h-24">
          <div class="flex-grow text-metal-400 overflow-y-auto">
            {{ alliance?.description || t('modals.viewAlliance.noDescription') }}
          </div>

          <!-- Action Buttons -->
          <div v-if="!isPlayerInThisAlliance && canRequestToJoin" class="absolute bottom-0 right-0">
            <ActionButton v-if="!hasRequestedToJoin" @click="requestJoinAlliance">
              {{ t('modals.viewAlliance.requestToJoin') }}
            </ActionButton>
            <ActionButton
              v-else
              @click="revokeJoinAlliance"
              class="bg-orange-700 hover:bg-orange-600"
            >
              {{ t('modals.viewAlliance.revokeRequest') }}
            </ActionButton>
          </div>
        </div>
      </div>

      <!-- Members List -->
      <StandardCard :title="t('modals.viewAlliance.members')" :noPadding="true">
        <div class="max-h-60 overflow-y-auto">
          <div class="divide-y divide-metal-700">
            <div v-for="memberId in memberIds" :key="memberId" class="flex items-center py-2 px-2">
              <div class="flex-grow">
                <PlayerName :playerId="memberId" />
              </div>
              <div class="flex-shrink-0 text-sm">
                <span
                  v-if="alliance && memberId === alliance.leaderPlayerId"
                  class="text-metal-400 px-2 py-0.5 text-xs"
                >
                  {{ t('modals.viewAlliance.leader') }}
                </span>
                <span v-else class="text-metal-400 px-2 py-0.5 text-xs"> {{ t('modals.viewAlliance.member') }} </span>
              </div>
            </div>
          </div>
        </div>
      </StandardCard>
    </div>
  </SmallModel>
</template>

<style scoped lang="scss"></style>
